import type { PostHog, PostHogConfig } from 'posthog-js'

/**
 * Lazy PostHog SDK holder.
 *
 * The SDK chunk is only requested once consent is given (`activatePostHog`). On revocation the
 * SDK stays in memory (it cannot be unloaded) but is opted out, reset and hidden from the rest of
 * the app: `getPostHog()` returns `null` until consent is given again.
 */

let sdk: PostHog | null = null
let sdkLoading: Promise<PostHog> | null = null
let sdkInitialized = false
let activeClient: PostHog | null = null

const loadSdk = (): Promise<PostHog> => {
  if (sdk) {
    return Promise.resolve(sdk)
  }
  if (!sdkLoading) {
    sdkLoading = import(/* webpackChunkName: "posthog" */ 'posthog-js').then(module => {
      sdk = module.default
      return sdk
    })
  }
  return sdkLoading
}

export const activatePostHog = async(token: string, options: Partial<PostHogConfig>): Promise<PostHog> => {
  const posthog = await loadSdk()

  if (!sdkInitialized) {
    posthog.init(token, options)
    sdkInitialized = true
  }

  // opt_out_capturing() persists across reloads, so a re-consent has to opt in explicitly.
  if (posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing({ captureEventName: null })
  }

  activeClient = posthog
  return posthog
}

export const deactivatePostHog = (): void => {
  if (!sdk || !sdkInitialized) {
    return
  }
  if (!sdk.has_opted_out_capturing()) {
    sdk.opt_out_capturing()
  }
  sdk.reset()
  activeClient = null
}

/** The PostHog client, or `null` while the user has not consented. */
export const getPostHog = (): PostHog | null => activeClient
